import { css } from '@emotion/react'
import {
  CornerRadius,
  cssVar,
  gemVar,
  LineWeight,
  Spacing,
  ThemedColors,
  Typography,
} from '@opallabs/gem'
import { type ThemedGemVars } from '@opallabs/gem/src/Color/types'
import { PillSizes, type PillColors } from './types'

const rootClass = 'lapidary-pill'
export const selectors = {
  root: rootClass,
  text: `${rootClass}-text`,
} as const
export const cssVariables = {
  color: `--${rootClass}-color`,
} as const
export const stateVariables = {
  hasIcon: 'has-icon',
  solid: 'is-solid',
  truncateText: 'is-truncated',
} as const

export const colors: Record<PillColors, ThemedGemVars> = {
  blue: ThemedColors.StrokePrimary2,
  darkGray: ThemedColors.IconSecondary,
  gray: ThemedColors.Surface3,
  green: ThemedColors.StrokeSuccess1,
  purple: ThemedColors.Info1,
  red: ThemedColors.Error1,
} as const

export const iconSize: Record<PillSizes, number> = {
  large: Spacing.XS * 5,
  small: Spacing.XS * 4,
  xsmall: Spacing.XS * 3,
} as const

export const root = css({
  alignItems: 'center',
  backgroundColor: gemVar(ThemedColors.Surface1),
  borderColor: cssVar(cssVariables.color),
  borderStyle: 'solid',
  borderWidth: LineWeight.Large,
  boxSizing: 'border-box',
  color: gemVar(ThemedColors.TextPrimary),
  columnGap: Spacing.XS,
  contain: 'content',
  display: 'flex',
  flexShrink: 0,
  inlineSize: 'fit-content',
  justifyContent: 'center',
  paddingInline: Spacing.S,

  ...Object.fromEntries(
    (Object.keys(colors) as PillColors[]).map((color) => [
      `&.color-${color}`,
      { [cssVariables.color]: gemVar(colors[color]) },
    ])
  ),
  [`&.${stateVariables.solid}`]: {
    backgroundColor: cssVar(cssVariables.color),
    color: gemVar(ThemedColors.TextContrast),
  },
  [`&.${stateVariables.solid}.color-gray`]: {
    color: gemVar(ThemedColors.TextPrimary),
  },
  [`&.${stateVariables.truncateText}`]: {
    flexShrink: 1,

    [`.${selectors.text}`]: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      // Fiddle with spacing to avoid clipping ascenders and descenders
      marginBlock: '-0.5em',
      paddingBlock: '0.5em',
    },
  },
  '&.size-large': {
    ...Typography.H100,
    borderRadius: CornerRadius.Large * 2,
    blockSize: Spacing.M * 2,
    lineHeight: 1,
    paddingBlock: Spacing.XS * 1.5,
  },
  '&.size-small': {
    ...Typography.H50,
    borderRadius: CornerRadius.XLarge,
    blockSize: Spacing.L,
    lineHeight: 1,
    paddingBlock: Spacing.XS / 2,
  },
  '&.size-xsmall': {
    ...Typography.T300,
    borderRadius: CornerRadius.XSmall * 5,
    blockSize: Spacing.M,
    columnGap: Spacing.XS / 2,
    lineHeight: 1,
    paddingInline: Spacing.XS,
    whiteSpace: 'nowrap',
  },
  [`&.${stateVariables.hasIcon}.size-xsmall`]: {
    paddingInlineStart: Spacing.XS / 2,
  },

  [`@container (max-inline-size: ${Spacing.L * 15}px)`]: {
    maxInlineSize: '100%',
  },

  [`.${selectors.text}`]: {
    textBox: 'trim-both cap alphabetic',
  },
  // <jcole@> As of 2026-05-11 Firefox doesn't support `text-box` or the properties it's shorthand for
  '@supports not (text-box-trim: trim-both)': {
    [`.${selectors.text}`]: {
      paddingBlockStart: 1,
    },
  },
})
